// Задачи: список с фильтрами, создание, назначение, выполнение.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckSquare, Plus, Check, RotateCcw, AlertTriangle, User as UserIcon, Calendar as CalIcon,
} from 'lucide-react';
import { tasksApi, chatApi, objectsApi, contractsApi, getErrorMessage } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { formatDate } from '@/lib/format';
import type { Task } from '@/types';

const STATUS_COLOR: Record<string, string> = {
  open: 'var(--blue)', in_progress: 'var(--yellow)', done: 'var(--green)', cancelled: 'var(--text-muted)',
};
const PRIORITY_COLOR: Record<string, string> = {
  low: 'var(--text-muted)', normal: 'var(--text-secondary)', high: 'var(--yellow)', urgent: 'var(--red)',
};
const FILTERS = [
  { key: '', label: 'Все' },
  { key: 'mine', label: 'Мои' },
  { key: 'open', label: 'Открытые' },
  { key: 'in_progress', label: 'В работе' },
  { key: 'done', label: 'Выполненные' },
];

export function TasksPage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data: counts } = useQuery({ queryKey: ['task-counts'], queryFn: () => tasksApi.counts() });
  const { data: tasks, isLoading } = useQuery({
    queryKey: ['tasks', filter],
    queryFn: () => tasksApi.list(
      filter === 'mine' ? { mine: true }
        : filter ? { status: filter } : {}
    ),
  });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['tasks'] }); qc.invalidateQueries({ queryKey: ['task-counts'] }); };
  const completeM = useMutation({ mutationFn: (id: number) => tasksApi.complete(id),
    onSuccess: () => { invalidate(); toast('Задача выполнена', 'success'); }, onError: (e) => toast(getErrorMessage(e), 'error') });
  const reopenM = useMutation({ mutationFn: (id: number) => tasksApi.reopen(id),
    onSuccess: invalidate, onError: (e) => toast(getErrorMessage(e), 'error') });

  return (
    <AppLayout>
      <PageHeader title="Задачи"
        subtitle={counts ? `${counts.open} открытых · ${counts.overdue} просрочено` : undefined}
        actions={<Button variant="primary" onClick={() => setCreateOpen(true)}><Plus size={16} /> Новая задача</Button>} />

      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              padding: '6px 14px', borderRadius: 16, fontSize: 13, cursor: 'pointer',
              border: '1px solid var(--border-bright)',
              background: filter === f.key ? 'var(--accent)' : 'transparent',
              color: filter === f.key ? '#1a1205' : 'var(--text-secondary)',
              fontWeight: filter === f.key ? 500 : 400,
            }}>
            {f.label}
            {f.key === 'mine' && counts && counts.mine > 0 && ` (${counts.mine})`}
          </button>
        ))}
      </div>

      {isLoading ? <Spinner label="Загрузка задач…" /> :
        !tasks || tasks.length === 0 ? (
          <EmptyState icon={<CheckSquare size={32} />} title="Задач нет" hint="Создайте первую задачу" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tasks.map((t: Task) => (
              <div key={t.id} className="panel" style={{ padding: 16, borderLeft: `3px solid ${PRIORITY_COLOR[t.priority]}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 15, fontWeight: 500, textDecoration: t.status === 'done' ? 'line-through' : 'none', color: t.status === 'done' ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                        {t.title}
                      </span>
                      <StatusBadge label={t.status_display} color={STATUS_COLOR[t.status]} />
                      {t.priority === 'urgent' && <StatusBadge label="Срочно" color="var(--red)" />}
                      {t.is_overdue && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--red)', fontSize: 12 }}>
                          <AlertTriangle size={13} /> просрочено
                        </span>
                      )}
                    </div>
                    {t.description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>{t.description}</div>}
                    <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                      {t.assignee_name && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UserIcon size={12} /> {t.assignee_name}</span>}
                      {t.due_date && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CalIcon size={12} /> {formatDate(t.due_date)}</span>}
                      {t.object_address && <span>🏢 {t.object_address}</span>}
                      {t.contract_number && <span>📄 {t.contract_number}</span>}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {t.status === 'done' ? (
                      <Button size="sm" variant="ghost" onClick={() => reopenM.mutate(t.id)}><RotateCcw size={14} /> Вернуть</Button>
                    ) : (
                      <Button size="sm" variant="primary" onClick={() => completeM.mutate(t.id)}><Check size={14} /> Выполнить</Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      <CreateTaskModal open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={() => { invalidate(); toast('Задача создана', 'success'); }}
        onError={(m) => toast(m, 'error')} />
    </AppLayout>
  );
}

function CreateTaskModal({ open, onClose, onCreated, onError }: {
  open: boolean; onClose: () => void; onCreated: () => void; onError: (m: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignee, setAssignee] = useState<number | ''>('');
  const [priority, setPriority] = useState('normal');
  const [dueDate, setDueDate] = useState('');
  const [relObject, setRelObject] = useState<number | ''>('');

  const { data: contacts } = useQuery({ queryKey: ['chat-contacts'], queryFn: () => chatApi.contacts(), enabled: open });
  const { data: objects } = useQuery({ queryKey: ['objects-all'], queryFn: () => objectsApi.list({}), enabled: open });

  const m = useMutation({
    mutationFn: () => tasksApi.create({
      title, description,
      assignee: assignee || null, priority: priority as Task['priority'],
      due_date: dueDate || null, related_object: relObject || null,
    }),
    onSuccess: () => { onClose(); reset(); onCreated(); },
    onError: (e) => onError(getErrorMessage(e)),
  });
  const reset = () => { setTitle(''); setDescription(''); setAssignee(''); setPriority('normal'); setDueDate(''); setRelObject(''); };

  return (
    <Modal open={open} onClose={onClose} title="Новая задача"
      footer={<>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled={!title || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Создание…' : 'Создать'}
        </Button>
      </>}>
      <div className="field"><label className="field__label">Название</label>
        <input className="field__input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Что нужно сделать" /></div>
      <div className="field"><label className="field__label">Описание</label>
        <textarea className="field__textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 12 }}>
        <div className="field" style={{ flex: 1 }}><label className="field__label">Исполнитель</label>
          <select className="field__select" value={assignee} onChange={(e) => setAssignee(e.target.value ? Number(e.target.value) : '')}>
            <option value="">— не назначен —</option>
            {contacts?.map((c) => <option key={c.id} value={c.id}>{c.short_name}</option>)}
          </select></div>
        <div className="field" style={{ flex: 1 }}><label className="field__label">Приоритет</label>
          <select className="field__select" value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="low">Низкий</option><option value="normal">Обычный</option>
            <option value="high">Высокий</option><option value="urgent">Срочный</option>
          </select></div>
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <div className="field" style={{ flex: 1 }}><label className="field__label">Срок</label>
          <input type="date" className="field__input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
        <div className="field" style={{ flex: 1 }}><label className="field__label">Объект</label>
          <select className="field__select" value={relObject} onChange={(e) => setRelObject(e.target.value ? Number(e.target.value) : '')}>
            <option value="">— не привязан —</option>
            {objects?.results.map((o) => <option key={o.id} value={o.id}>{o.address}</option>)}
          </select></div>
      </div>
    </Modal>
  );
}
